
import {
  computed,
  defineComponent,
  onUpdated,
  PropType,
  Ref,
  ref,
  SetupContext,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import AutoComplete from 'primevue/autocomplete';
import { DomHandler } from 'primevue/utils';
import SegmentValuesDialog from '@/components/segmentations/fields/SegmentValuesDialog.vue';

// Values still shown in the field once it is compact: the last ones, which
// Backspace removes first
const COMPACT_VISIBLE_VALUES = 2;

export default defineComponent({
  name: 'SegmentAutoComplete',

  components: {
    AutoComplete,
    SegmentValuesDialog,
  },

  props: {
    modelValue: {
      type: Object as PropType<string[]>,
      required: true,
    },

    options: {
      type: Function,
      required: true,
    },

    multiple: {
      type: Boolean,
      required: false,
      default: true,
    },

    field: {
      type: String,
      required: true,
    },

    // A list pasted over several lines gives one value per line
    splitPastedLines: {
      type: Boolean,
      required: false,
      default: false,
    },

    // A value typed but not picked is kept when leaving the field
    addOnBlur: {
      type: Boolean,
      required: false,
      default: false,
    },

    // Values shown as tags before the field turns compact (from 3 values on)
    maxVisible: {
      type: Number,
      required: false,
      default: 2,
    },
  },

  emits: ['update:modelValue'],

  setup(props: { modelValue: string[]; options: Function; multiple: boolean; splitPastedLines: boolean; addOnBlur: boolean; maxVisible: number }, { emit }: SetupContext) {
    const { t } = useI18n();
    const filteredItems = ref();
    const inputs: Ref<string[]> = ref(props.modelValue ?? []);
    const managerVisible = ref(false);

    const valuesCount = computed(() => (Array.isArray(inputs.value) ? inputs.value.length : 0));
    const compact = computed(() => props.multiple && valuesCount.value > props.maxVisible);
    const hiddenCount = computed(() => valuesCount.value - COMPACT_VISIBLE_VALUES);

    // The window gives back the whole list, emitted like any other change
    const onManagerValidate = (values: string[]) => {
      inputs.value = values;
    };

    const handleComplete = ({ query }: { query: string }) => {
      if (!inputs.value.includes(query)) {
        filteredItems.value = [query];
      } else {
        filteredItems.value = [];
      }
    };

    // Adds the values missing from the list, trimmed, blank ones dropped
    const addValues = (values: string[]) => {
      const current = Array.isArray(inputs.value) ? inputs.value : [];
      const added = values
        .map((value) => value.trim())
        .filter((value, index, all) => value !== '' && all.indexOf(value) === index && !current.includes(value));
      if (added.length > 0) {
        inputs.value = [...current, ...added];
      }
    };

    const handlePaste = (event: ClipboardEvent) => {
      const text = event.clipboardData?.getData('text') ?? '';
      if (!/\r?\n/.test(text.trim())) {
        return;
      }
      event.preventDefault();
      addValues(text.split(/\r?\n/));
    };

    const handleBlur = (event: FocusEvent) => {
      if (!props.addOnBlur) {
        return;
      }
      const input = event.target as HTMLInputElement;
      addValues([input.value]);
      input.value = '';
    };

    const inputProps = props.splitPastedLines ? { onPaste: handlePaste } : undefined;

    // A list of free values: wider field, long values cut
    const listMode = props.splitPastedLines || props.addOnBlur;

    watch(inputs, () => {
      // A value picked twice is kept once: PrimeVue does not block a repeated
      // pick in multiple mode, and a suggestion clicked right after leaving the
      // field would otherwise be added by both
      if (Array.isArray(inputs.value)) {
        const values = inputs.value;
        const unique = values.filter((value, index) => values.indexOf(value) === index);
        if (unique.length !== values.length) {
          inputs.value = unique;
          return;
        }
      }
      emit('update:modelValue', inputs.value);
    });

    // todo: have to recheck this
    // Bug in primevue autoHighlight functionality, select first element manually
    onUpdated(() => {
      const firstItem: HTMLElement = document.querySelector('.p-autocomplete-items > li') as HTMLElement;
      if (firstItem) {
        DomHandler.addClass(firstItem, 'p-highlight');
      }
    });

    return {
      t,
      filteredItems,
      inputs,
      inputProps,
      listMode,
      compact,
      hiddenCount,
      managerVisible,
      handleComplete,
      handleBlur,
      onManagerValidate,
    };
  },
});
